import React, { useState } from "react";
import { Mail, MapPin, Phone, Send, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, error: "" });
    try {
      await api.post("/contact", form);
      setState({ loading: false, ok: true, error: "" });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setState({ loading: false, ok: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  return (
    <div className="container-x py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <span className="section-eyebrow">İletişim</span>
          <h1 className="font-display text-5xl md:text-6xl mt-4 leading-tight">
            Bize <span className="italic gold-gradient-text">yazın</span>.
          </h1>
          <p className="mt-5 text-white/70 max-w-md leading-relaxed">
            Program seçiminde, sertifika sorularında veya kurumsal eğitim taleplerinde 
            ekibimiz size 24 saat içinde geri döner.
          </p>

          <div className="mt-12 space-y-6">
            <Info icon={<Mail className="w-5 h-5" />} label="E-posta" value="info@zbcampus.com" />
            <Info icon={<Phone className="w-5 h-5" />} label="WhatsApp" value="0554 329 11 19" />
            <Info icon={<MapPin className="w-5 h-5" />} label="Çalışma Şekli" value="Tamamen Online" />
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="bg-surface border border-white/10 p-8 md:p-12 space-y-6">
            {state.ok && (
              <div className="flex gap-3 items-start bg-gold/10 border border-gold-30 p-4 text-sm">
                <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <div>Mesajınız iletildi. En kısa sürede size dönüş yapılacaktır.</div>
              </div>
            )}
            {state.error && (
              <div className="bg-red-950/40 border border-red-800 text-red-200 p-4 text-sm">{state.error}</div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Ad Soyad" required>
                <input data-testid="contact-name" required value={form.name} onChange={(e) => setField("name", e.target.value)} className="input-style" />
              </Field>
              <Field label="E-posta" required>
                <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} className="input-style" />
              </Field>
              <Field label="Telefon">
                <input data-testid="contact-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="input-style" />
              </Field>
              <Field label="Konu">
                <input data-testid="contact-subject" value={form.subject} onChange={(e) => setField("subject", e.target.value)} className="input-style" />
              </Field>
            </div>

            <Field label="Mesajınız" required>
              <textarea
                data-testid="contact-message"
                required
                rows={6}
                value={form.message}
                onChange={(e) => setField("message", e.target.value)}
                className="input-style resize-none"
              />
            </Field>

            <button
              data-testid="contact-submit"
              type="submit"
              disabled={state.loading}
              className="btn-primary disabled:opacity-60"
            >
              {state.loading ? "Gönderiliyor..." : "Mesajı Gönder"}
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>

      <style>{`
        .input-style {
          width: 100%;
          background: #0F0F14;
          border: 1px solid rgba(255,255,255,0.08);
          padding: 0.75rem 1rem;
          color: white;
          font-family: 'Outfit', sans-serif;
          transition: border-color 0.2s ease;
          outline: none;
        }
        .input-style:focus { border-color: rgba(212,175,55,0.6); }
        .input-style::placeholder { color: rgba(255,255,255,0.4); }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-white/60 mb-2 inline-block">
        {label} {required && <span className="text-gold">*</span>}
      </span>
      {children}
    </label>
  );
}

function Info({ icon, label, value }) {
  return (
    <div className="flex gap-4 items-start">
      <div className="w-11 h-11 border border-gold-30 flex items-center justify-center text-gold shrink-0">{icon}</div>
      <div>
        <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
        <div className="text-white mt-1">{value}</div>
      </div>
    </div>
  );
}
